import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import {
  ICreateEventIncome,
  IEventIncomeResponse,
  IUpdateEventIncome,
  IDeleteEventIncome,
} from './types';

export const useGetEventIncome = (
  id: string,
  options?: IResponseHandlerOptions<IEventIncomeResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEventIncomeResponse, DefaultError>({
    queryKey: ['event-income', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `EventIncome/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetEventIncome - ${id}`,
        notFoundPath: '/events',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const useGetEventIncomes = (eventId: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEventIncomeResponse[], DefaultError>({
    queryKey: ['event-incomes', eventId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `EventIncome/Event`,
        params: { eventId },
      });

      return responseHandler(response, {
        groupName: `useGetEventIncomes - ${eventId}`,
        notFoundPath: '/events',
      });
    },
    enabled: enabled,
  });
};

export const useCreateEventIncome = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('createEventIncome');

  return useMutation<IEventIncomeResponse, DefaultError, ICreateEventIncome>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: body.incomeName,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `EventIncome`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `createEventIncome`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.incomeName,
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateEventIncome = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('updateEventIncome');

  return useMutation<unknown, DefaultError, IUpdateEventIncome>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: body.incomeName,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `EventIncome`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `updateEventIncome`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('event-income.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteEventIncome = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('deleteEventIncome');

  return useMutation<unknown, DefaultError, IDeleteEventIncome>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.remove.loading', {
          what: i18next.t('event-income.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `EventIncome`,
        body,
      });

      return responseHandler(response, {
        groupName: `deleteEventIncome`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('event-income.what'),
        })
      );
    },
    onError: dismiss,
  });
};
